import { WalletController } from "@api/WalletController";
import { create } from "zustand";
import { persist } from "zustand/middleware";
import { TonApi } from "@api/TonApi";
import { fromNano } from "@ton/core";
import { addressToColor } from "@utils/addressToColor";
import { useAssets } from "./assets.store";

export interface IWallet {
    guid: string;
    name: string | null;
    rawAddress: string;
    friendlyAddress: string;
    contract: string;
    status: string;
    referralCode: string;
    referralPercent: number;
    isWhiteList: boolean;
    isMnemonics: boolean;
    type: "internal" | "external";
}

export interface ICurrency {
    name: string;
    value: number;
}

export interface IPortfolioItem {
    backgroundColor: string;
    name: string;
    tonPrice: number;
    usdPrice: number;
    jettonMasterAddress: string;
    symbol: string;
    image: string;
    guid: string;
    balance: number;
    walletGuid: string;
    decimals: number;
}
interface IReferral {
    rawAddress: string;
}

interface IUser {
    isConnected: boolean;
    jwt: string;
    setAuth(isConnected: boolean, jwt: string): void;
    externalWallet: IWallet;
    wallets: IWallet[];
    currentWallet: IWallet;
    currencies: ICurrency[];
    portfolioItems: IPortfolioItem[];
    referralPercent: number;
    referrals: IReferral[];
    isLoading: boolean;
    getWallets(): Promise<void>;
    setWallets(wallets: IWallet[]): void;
    setCurrentWallet(wallet: IWallet): void;
    fetchReferrals(): Promise<void>;
    fetchBalances(): Promise<void>;
    fetchPortfolioItems(): Promise<void>;
}

const initialCurrencies = [
    {
        name: "TON",
        value: 0,
    },
    {
        name: "USD",
        value: 0,
    },
    {
        name: "RUB",
        value: 0,
    },
];

// Default empty wallet to prevent undefined errors
const emptyWallet: IWallet = {
    guid: "",
    name: null,
    rawAddress: "",
    friendlyAddress: "",
    contract: "",
    status: "",
    referralCode: "",
    referralPercent: 0,
    isWhiteList: false,
    isMnemonics: false,
    type: "internal",
};

export const useWallet = create<IUser & { isMnemonic: () => boolean }>()(
    persist(
        (set, get) => ({
            isConnected: false,
            jwt: "",
            wallets: [],
            currentWallet: emptyWallet,
            referralPercent: 25,
            referrals: [],
            currencies: initialCurrencies,
            portfolioItems: [],
            isLoading: false,
            // Computed state for easy mnemonic check
            isMnemonic: () => !!get().currentWallet && !!get().currentWallet.isMnemonics,

            setAuth: (isConnected: boolean, jwt: string) => set(() => ({ isConnected, jwt })),
            externalWallet: emptyWallet,
            setWallets: (wallets: IWallet[]) => set(() => ({ wallets })),
            setCurrentWallet: (wallet: IWallet) => {
                set(() => ({ currentWallet: wallet }));
                // Fetch balances and portfolio items when wallet changes
                get().fetchBalances();
                get().fetchPortfolioItems();
            },
            getWallets: async () => {
                try {
                    const { data: walletsFromApi } = await WalletController.getWallets();

                    if (!walletsFromApi || walletsFromApi.length === 0) {
                        console.log("No wallets returned from API");
                        return;
                    }
                    // Map wallets to add 'type' property
                    const wallets: IWallet[] = walletsFromApi.map((w: any) => ({
                        ...w,
                        type: w.isMnemonics ? "internal" : "external",
                    }));
                    // Find external wallet (non-mnemonic)
                    const externalWallet = wallets.find((w: IWallet) => w.type === "external") || emptyWallet;

                    // Set current wallet to either existing one or external wallet
                    const currentWallet = get().currentWallet?.guid
                        ? wallets.find((w: IWallet) => w.guid === get().currentWallet.guid) || externalWallet
                        : externalWallet;

                    // Only update if values have changed
                    const prev = get();
                    const shallowEqual = (a: any, b: any) => {
                        if (Array.isArray(a) && Array.isArray(b)) {
                            if (a.length !== b.length) return false;
                            for (let i = 0; i < a.length; i++) {
                                if (a[i]?.guid !== b[i]?.guid) return false;
                            }
                            return true;
                        }
                        if (typeof a === "object" && typeof b === "object") {
                            return (
                                Object.keys(a).every((k) => a[k] === b[k]) && Object.keys(b).every((k) => a[k] === b[k])
                            );
                        }
                        return a === b;
                    };
                    if (
                        !shallowEqual(prev.wallets, wallets) ||
                        !shallowEqual(prev.externalWallet, externalWallet) ||
                        !shallowEqual(prev.currentWallet, currentWallet)
                    ) {
                        set(() => ({
                            wallets,
                            externalWallet,
                            currentWallet,
                        }));
                    }

                    // Fetch balances and portfolio items after getting wallets
                    if (currentWallet && currentWallet.friendlyAddress) {
                        get().fetchBalances();
                        get().fetchPortfolioItems();
                    }
                } catch (error) {
                    console.error("Error fetching wallets:", error);
                }
            },
            fetchBalances: async () => {
                try {
                    set(() => ({ isLoading: true }));
                    const wallet = get().currentWallet;

                    if (!wallet || !wallet.friendlyAddress) {
                        console.log("No wallet to fetch balances for");
                        set(() => ({ isLoading: false }));
                        return;
                    }

                    console.log("Fetching balances for wallet:", wallet.friendlyAddress);

                    // Create a new instance of TonApi
                    const tonApi = new TonApi();

                    // Get the TON balance
                    const tonBalance = await tonApi.getTonBalance(wallet.friendlyAddress);

                    // Get the exchange rates
                    const rates = await tonApi.getTonRates();

                    // Calculate the TON amount
                    const tonAmount = Number(fromNano(tonBalance.balance));

                    // Get the USD and RUB rates
                    const { RUB = 0, USD = 0 } = rates.TON.prices || {};

                    const currencies = [
                        {
                            name: "TON",
                            value: tonAmount,
                        },
                        {
                            name: "USD",
                            value: USD * tonAmount,
                        },
                        {
                            name: "RUB",
                            value: RUB * tonAmount,
                        },
                    ];

                    let copyPortfolioItems = [...get().portfolioItems];
                    copyPortfolioItems.push({
                        jettonMasterAddress: "ton",
                        symbol: "TON",
                        image: "https://ton.org/download/ton_symbol.svg",
                        balance: tonAmount,
                        walletGuid: wallet.guid,
                        decimals: 9,
                        guid: "",
                        name: "Toncoin",
                        backgroundColor: addressToColor("ton", "TON"),
                        tonPrice: tonAmount,
                        usdPrice: USD * tonAmount,
                    });

                    set(() => ({
                        portfolioItems: copyPortfolioItems,
                        currencies,
                        isLoading: false,
                    }));
                } catch (error) {
                    console.error("Error fetching balances:", error);
                    set(() => ({ isLoading: false }));
                }
            },
            fetchPortfolioItems: async () => {
                try {
                    set(() => ({ isLoading: true }));
                    const wallet = get().currentWallet;

                    if (!wallet || !wallet.friendlyAddress) {
                        set(() => ({ isLoading: false }));
                        return;
                    }
                    const tonApi = new TonApi();

                    const { balances: tonBalances }: { balances: any[] } = await tonApi.getAddressJettons(
                        wallet.friendlyAddress,
                    );

                    const assets: IPortfolioItem[] | any = await useAssets.getState().findAssets(
                        tonBalances.map((tb: any) => ({
                            jettonMasterAddress: tb.jetton.address.toString(),
                        })),
                    );

                    if (!assets || (typeof assets === "object" && "status" in assets && assets.status === 429)) {
                        console.warn("Portfolio API rate limit hit or invalid response, skipping processing.");
                        set(() => ({ isLoading: false }));
                        return;
                    }

                    if (!Array.isArray(assets)) {
                        console.warn("Invalid response from Portfolio API, skipping processing.");
                        set(() => ({ isLoading: false }));
                        return;
                    }

                    const portfolioItems = assets.map((asset: any) => {
                        const { jettonMasterAddress, symbol, image, guid, decimals } = asset;
                        const compiledAsset = tonBalances.find(
                            (tb: any) => tb.jetton.address.toString() === jettonMasterAddress,
                        );

                        if (!compiledAsset)
                            return {
                                backgroundColor: addressToColor(jettonMasterAddress, symbol),
                                name: symbol,
                                tonPrice: 0,
                                usdPrice: 0,
                                jettonMasterAddress,
                                symbol,
                                image,
                                guid,
                                walletGuid: wallet.guid,
                                balance: 0,
                                decimals,
                            };

                        const balance = parseInt(compiledAsset.balance) * 10 ** -(decimals ?? 9);

                        return {
                            backgroundColor: addressToColor(jettonMasterAddress, symbol),
                            name: symbol,
                            tonPrice: 0,
                            usdPrice: 0,
                            jettonMasterAddress,
                            symbol,
                            image,
                            guid,
                            walletGuid: wallet.guid,
                            balance: balance,
                            decimals,
                        };
                    });

                    set(() => ({
                        portfolioItems,
                        isLoading: false,
                    }));
                } catch (error: any) {
                    console.error("Error fetching portfolio items:", error);
                    set(() => ({ isLoading: false, portfolioItems: [] }));
                }
            },
            fetchReferrals: async () => {
                try {
                    const { data: refferals } = await WalletController.getReferralsList();

                    const { data: referralPercent } = await WalletController.getReferralConditions();

                    if (!refferals || refferals.length === 0) {
                        console.log("No referrals returned from API");
                        return;
                    }

                    set(() => ({
                        referrals: refferals.referrals,
                        referralPercent: referralPercent.referralPercent,
                    }));
                } catch (error: any) {
                    console.error("Error fetching referrals:", error);
                    set(() => ({ isLoading: false, referrals: [] }));
                }
            },
        }),
        {
            name: "wallet-storage",
            partialize: (state: IUser & { isMnemonic: () => boolean }) => ({
                isConnected: state.isConnected,
                jwt: state.jwt,
                wallets: state.wallets,
                currentWallet: state.currentWallet,
            }),
        },
    ),
);
